// Résolution LOCALE et DÉTERMINISTE de la langue de sortie (CV + lettre).
//
// Règle produit :
//  1. Choix explicite de l'utilisateur (Options / panneau preview) -> ce choix.
//  2. Sinon : ANGLAIS par défaut, avec une seule exception — si l'offre est
//     rédigée dans une langue que le candidat PARLE (champ `languages` du
//     profil), le document sort dans cette langue.
//
// La détection était auparavant déléguée au modèle dans le prompt (« détecte la
// langue de l'offre... ») : re-tirée à chaque appel, elle rendait la langue
// NON déterministe (regénérer une offre EN pouvait basculer en FR, biais du
// prompt/profil français). Ici tout est résolu côté client, une bonne fois,
// et les prompts reçoivent TOUJOURS une langue concrète.

/** Langues détectables. Le reste du monde retombe sur l'anglais (défaut). */
export type DetectedLanguage = 'fr' | 'en' | 'nl' | 'de'

/** Libellé injecté dans les prompts (cohérent avec les valeurs historiques
 *  de `settings.language` : 'Français' / 'Anglais'). */
const LANGUAGE_LABELS: Record<DetectedLanguage, string> = {
  fr: 'Français',
  en: 'Anglais',
  nl: 'Néerlandais',
  de: 'Allemand',
}

// Mots-outils DISTINCTIFS par langue. Les formes partagées entre langues sont
// volontairement exclues (de/en FR-NL, of EN-NL, in/is/die/als NL-DE-EN…) pour
// que chaque hit soit un vrai signal. On compte la MASSE : les libellés
// LinkedIn injectés en français (« À propos de l'offre d'emploi ») ne pèsent
// rien face au corps d'une offre.
const STOPWORDS: Record<DetectedLanguage, RegExp> = {
  en: /\b(the|and|you|your|our|we|will|with|for|are|this|that|have|from|to|be|or|at|by)\b/g,
  fr: /\b(le|la|les|des|du|une|et|est|dans|pour|avec|sur|vous|nous|votre|vos|notre|que|qui|sont|au|aux|ce|cette)\b/g,
  nl: /\b(het|een|van|voor|met|aan|je|jouw|bij|naar|wordt|zijn|onze|ons|niet|dat|ook|om)\b/g,
  de: /\b(der|das|und|wir|sie|mit|für|ein|eine|ist|sind|von|zu|den|dem|im|auf|werden|ihre|sich|nicht|oder)\b/g,
}

/**
 * Langue dominante d'un texte, par comptage de mots-outils distinctifs.
 * `null` si le signal est trop faible ou trop ambigu — l'appelant retombe
 * alors sur l'anglais (le défaut sûr de la règle produit).
 */
export function detectDominantLanguage(text: string): DetectedLanguage | null {
  if (!text) return null
  const t = text.toLowerCase()
  const scores = (Object.keys(STOPWORDS) as DetectedLanguage[]).map((lang) => ({
    lang,
    hits: (t.match(STOPWORDS[lang]) ?? []).length,
  }))
  scores.sort((a, b) => b.hits - a.hits)
  const [first, second] = scores
  // Seuils : au moins 5 hits, et une vraie dominance sur la 2e langue.
  if (first.hits < 5 || first.hits < second.hits * 1.3) return null
  return first.lang
}

// Orthographes acceptées pour reconnaître une langue dans profile.languages
// (le profil peut être rédigé en FR comme en EN : "Français" / "French"…).
// Comparées après minusculisation + suppression des accents, token par token.
const LANGUAGE_NAMES: Record<DetectedLanguage, string[]> = {
  fr: ['francais', 'french', 'fr'],
  en: ['anglais', 'english', 'en'],
  nl: ['neerlandais', 'nederlands', 'dutch', 'nl', 'flamand', 'vlaams'],
  de: ['allemand', 'deutsch', 'german', 'de'],
}

const normalize = (s: string): string =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')

/** Profil minimal requis : seule la liste des langues parlées compte ici. */
export interface SpokenLanguagesProfile {
  languages?: Array<{ language?: string }>
}

/** true si `lang` figure dans les langues du profil (toutes orthographes). */
export function speaksLanguage(profile: SpokenLanguagesProfile | null | undefined, lang: DetectedLanguage): boolean {
  const names = LANGUAGE_NAMES[lang]
  return (profile?.languages ?? []).some((entry) => {
    const tokens = normalize(entry?.language ?? '').split(/[^a-z]+/)
    return tokens.some((tok) => names.includes(tok))
  })
}

// Noms d'une langue tels qu'une OFFRE les écrit, dans les quatre langues
// détectables, après `normalize`. Distincts de LANGUAGE_NAMES : on n'y met pas
// les codes à deux lettres (« de », « en » sont des mots-outils dans un texte).
const NOMS_DANS_UNE_OFFRE: Record<DetectedLanguage, string[]> = {
  fr: ['francais', 'french', 'frans', 'franstalig', 'franzosisch'],
  en: ['anglais', 'english', 'engels', 'engelstalig', 'englisch'],
  nl: ['neerlandais', 'nederlands', 'nederlandstalig', 'dutch', 'niederlandisch', 'flamand', 'vlaams'],
  de: ['allemand', 'german', 'duits', 'duitstalig', 'deutsch'],
}
// Ce qui peut séparer deux langues proposées AU CHOIX : « Frans of Nederlands »,
// « Nederlands en/of het Frans », « français ou néerlandais », « French or
// Dutch ». Un « et » ou un « en » seul veut dire les deux : il n'y figure pas.
const SEPARATEUR_AU_CHOIX = /^[\s,]*(?:ou|of|or|oder|en\s*\/\s*of|et\s*\/\s*ou|and\s*\/\s*or|und\s*\/\s*oder|\/)\s*(?:het|le|la|l'|de|the)?\s*$/

/**
 * Une langue que le candidat parle, proposée par l'offre COMME ALTERNATIVE à
 * `lang` (« néerlandais ou français »), ou `null`.
 *
 * Calculé dans le code et non laissé au prompt : sur le banc du 2026-10-14
 * (docs/analyse-career-ops.md), une consigne « sauf si l'offre accepte une autre
 * langue » n'a rien changé, le modèle citait la phrase puis appliquait quand
 * même « offre en néerlandais = néerlandais exigé ». Même leçon que la langue
 * de l'offre elle-même : un fait calculé bat une consigne.
 */
export function langueAuChoixParlee(
  text: string,
  lang: DetectedLanguage,
  profile: SpokenLanguagesProfile | null | undefined
): DetectedLanguage | null {
  const t = normalize(text ?? '')
  const mentions: { lang: DetectedLanguage; debut: number; fin: number }[] = []
  for (const l of Object.keys(NOMS_DANS_UNE_OFFRE) as DetectedLanguage[]) {
    const motif = new RegExp(`\\b(?:${NOMS_DANS_UNE_OFFRE[l].join('|')})\\b`, 'g')
    for (const m of t.matchAll(motif)) mentions.push({ lang: l, debut: m.index!, fin: m.index! + m[0].length })
  }
  mentions.sort((a, b) => a.debut - b.debut)
  // Chaîne de mentions séparées par « ou » : « Frans, Nederlands of Engels ».
  let groupe: DetectedLanguage[] = []
  const examiner = (): DetectedLanguage | null => {
    if (groupe.length < 2 || !groupe.includes(lang)) return null
    return groupe.find((l) => l !== lang && speaksLanguage(profile, l)) ?? null
  }
  for (let i = 0; i < mentions.length; i++) {
    const lie = i > 0 && SEPARATEUR_AU_CHOIX.test(t.slice(mentions[i - 1].fin, mentions[i].debut))
    if (!lie) {
      const trouve = examiner()
      if (trouve) return trouve
      groupe = []
    }
    groupe.push(mentions[i].lang)
  }
  return examiner()
}

/**
 * Langue de sortie EFFECTIVE d'un document pour une offre donnée.
 *
 * @param choice Valeur de `settings.language` : un libellé concret
 *   ('Français', 'Anglais'…) prime toujours ; vide ou 'Automatique' applique
 *   la règle (anglais par défaut, langue de l'offre si parlée).
 * @returns Un libellé TOUJOURS concret, prêt pour `languageInstruction`.
 *
 * Fonction pure : mêmes entrées -> même langue. C'est elle qui garantit qu'une
 * regénération ne change plus jamais de langue.
 */
export function resolveOutputLanguage(
  jobDescription: string,
  profile: SpokenLanguagesProfile | null | undefined,
  choice?: string
): string {
  if (choice && choice !== 'Automatique') return choice
  const detected = detectDominantLanguage(jobDescription)
  if (!detected || detected === 'en') return LANGUAGE_LABELS.en
  return speaksLanguage(profile, detected) ? LANGUAGE_LABELS[detected] : LANGUAGE_LABELS.en
}

/**
 * Langue dans laquelle un CV est RÉDIGÉ (accroche et puces), pas celles qu'il
 * dit parler. Sert à reconnaître une traduction entre le CV maître et le CV
 * adapté (issue #178) : comparées mot à mot, des phrases traduites passaient
 * pour retirées puis ajoutées.
 */
export function langueDuCv(cv: { basics?: { summary?: string }; work?: Array<{ summary?: string; highlights?: string[] }> } | null | undefined): DetectedLanguage | null {
  if (!cv) return null
  const texte = [cv.basics?.summary ?? '', ...(cv.work ?? []).flatMap((w) => [w.summary ?? '', ...(w.highlights ?? [])])].join(' ')
  return detectDominantLanguage(texte)
}
